import {
  ID,
  ScheduleEntryState,
  Task,
  TaskType,
  Timestamp,
  UpdateTask,
} from '@griply/types';
import {
  resolveTaskCompletionActions,
  TaskCompletionAction,
  TaskCompletionActionKind,
} from '@griply/utils';
import { useCallback } from 'react';

export type TaskTodayCompleteOptions = {
  value?: number;
  increment?: number;
  referenceDate?: Timestamp;
  toggleDayEntry?: boolean;
};

export type UseTaskTodayControlsHandlers = {
  updateTask: (update: UpdateTask) => void;
  updateHabitSchedules: (updates: UpdateTask[]) => void;
  updateGoalTaskCount: (args: { goalId: ID; delta: 1 | -1 }) => void;
  openManualEntry: (habitId: ID, date: Timestamp) => void;
  trackTaskComplete: (taskType: TaskType) => void;
};

export type UseTaskTodayControlsParams = {
  today: Timestamp;
  handlers: UseTaskTodayControlsHandlers;
};

export const useTaskTodayControls = ({
  today,
  handlers,
}: UseTaskTodayControlsParams) => {
  const onComplete = useCallback(
    (
      task: Task,
      type: ScheduleEntryState = ScheduleEntryState.Complete,
      options: TaskTodayCompleteOptions = {},
    ): TaskCompletionAction[] => {
      const actions = resolveTaskCompletionActions(task, {
        today,
        type,
        value: options.value,
        increment: options.increment,
        referenceDate: options.referenceDate,
        toggleDayEntry: options.toggleDayEntry,
      });

      for (const action of actions) {
        switch (action.kind) {
          case TaskCompletionActionKind.UpdateTask:
            handlers.updateTask(action.update);
            break;
          case TaskCompletionActionKind.UpdateHabitSchedules:
            handlers.updateHabitSchedules(action.updates);
            break;
          case TaskCompletionActionKind.UpdateGoalTaskCount:
            handlers.updateGoalTaskCount({
              goalId: action.goalId,
              delta: action.delta,
            });
            break;
          case TaskCompletionActionKind.OpenManualEntry:
            handlers.openManualEntry(action.habitId, action.date);
            break;
          case TaskCompletionActionKind.TrackTaskComplete:
            handlers.trackTaskComplete(action.taskType);
            break;
        }
      }

      return actions;
    },
    [today, handlers],
  );

  const openManualEntryLogging = useCallback(
    (habitId: Task['id'], date: Timestamp) => {
      handlers.openManualEntry(habitId, date);
    },
    [handlers],
  );

  return { onComplete, openManualEntryLogging };
};
